import type {
  DatadogRum,
  RumEvent,
  RumInitConfiguration,
} from '@datadog/browser-rum';
import {
  PASSKEY_REGISTER_EXPERIMENT,
  PASSKEY_REGISTER_POST_LOGIN_EXPERIMENT,
  type PasskeyRegistrationRumExperiment,
} from './passkeyRegistrationConstants';
import type { SafeClientErrorContext } from './reportClientError';
import {
  redactClientErrorString,
  reportClientError,
  sanitizeClientErrorUrl,
  setClientErrorReporter,
} from './reportClientError';

interface ClientMonitoringConfig {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  env: string;
  version: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  excludedActivityUrls: NonNullable<
    RumInitConfiguration['excludedActivityUrls']
  >;
}

type MutableRumEvent = RumEvent & {
  action?: {
    target?: {
      name?: string;
    };
  };
  context?: Record<string, unknown>;
  error?: {
    message?: string;
    source?: string;
    stack?: string;
  };
  resource?: {
    url?: string;
  };
  view?: {
    referrer?: string;
    url?: string;
  };
};

const DEFAULT_DATADOG_SITE = 'datadoghq.com';
const DEFAULT_DATADOG_SERVICE = 'descope-flows';
const DEFAULT_SESSION_SAMPLE_RATE = 100;
const DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 0;
const DEFAULT_EXCLUDED_ACTIVITY_URLS: NonNullable<
  RumInitConfiguration['excludedActivityUrls']
> = [
  /^https:\/\/api2\.amplitude\.com\/2\/httpapi(?:[?#]|$)/,
  /^https:\/\/featureassets\.org\/v1\/initialize(?:[?#]|$)/,
  /^https:\/\/www\.googletagmanager\.com\/gtm\.js(?:[?#]|$)/,
];
const STATSIG_NETWORK_ERROR_PATTERN = /\[Statsig\].*networking error occurred/i;
const RESIZE_OBSERVER_LOOP_ERROR_PATTERN =
  /ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)/i;
const GENERIC_CROSS_ORIGIN_SCRIPT_ERROR_MESSAGES = new Set([
  'Script error.',
  'Uncaught "Script error."',
  "Uncaught 'Script error.'",
  'Uncaught browser error Script error.',
]);
const WEBKIT_MESSAGE_HANDLERS_ERROR_PATTERN =
  /window\.webkit\.messageHandlers/i;
const CIRCULAR_REACT_DOM_NODE_ERROR_PATTERN =
  /Converting circular structure to JSON[\s\S]*starting at object with constructor 'HTML[A-Za-z]*Element'[\s\S]*__reactFiber\$[\s\S]*stateNode' closes the circle/i;
const ANONYMOUS_APPEND_CHILD_JSON_STACK_PATTERN =
  /at JSON\.stringify @ <anonymous>[\s\S]*at HTML[A-Za-z]*Element\.appendChild @ <anonymous>:\d+:\d+/i;
const EXPECTED_DESCOPE_ERROR_PATTERNS = [
  /Illegal email address/i,
  /Failed to verify email code/i,
  /Let's double-check your (email address|details|password|username)/i,
  /Max attempts exceeded for one time code/i,
  /OAuth cancell?ed by user/i,
  /One time code expired/i,
  /One time code is invalid/i,
  /(?:Password signin failed|Invalid signin credentials|User not found) Wrong password or unknown user/i,
  /That email doesn't match/i,
  /That username is already taken/i,
  /This email is already in use/i,
  /We couldn't validate this email address/i,
];
const DESCOPE_RESULT_FAIL_ERROR_PATTERN =
  /^\[Descope\]\s+"?map\[[\s\S]*\bresult:fail\b[\s\S]*\]"?(?:\s+Error:?)?\s*$/i;
const DESCOPE_UI_LOAD_FAILURE_MESSAGE = 'DescopeUI was not loaded';
const DESCOPE_UI_SCRIPT_ID_PREFIX = 'npmlib-descopeweb-components-ui-';
const DESCOPE_UI_SCRIPT_SELECTOR = `script[id^="${DESCOPE_UI_SCRIPT_ID_PREFIX}"], script[src*="/npm/@descope/web-components-ui@"]`;
const DESCOPE_UI_RESOURCE_PATH_PATTERN = /\/npm\/@descope\/web-components-ui@/;

let hasInitializedClientMonitoring = false;
let removeGlobalErrorHandlers: (() => void) | undefined;
let clientTimingReporter: Pick<DatadogRum, 'addTiming'> | undefined;
let clientRumReporter:
  | Pick<
      DatadogRum,
      | 'addAction'
      | 'addFeatureFlagEvaluation'
      | 'getInternalContext'
      | 'setTrackingConsent'
      | 'startSessionReplayRecording'
      | 'stopSession'
      | 'stopSessionReplayRecording'
    >
  | undefined;
let pendingClientTimings: Array<{ name: string; time: number }> = [];

export type PasskeyRegistrationRumVariant = 'control' | 'offer_passkey';
export type { PasskeyRegistrationRumExperiment };
export type PasskeyEnrollmentRumState =
  | 'enrollment_offered'
  | 'enrollment_started'
  | 'enrollment_cancelled'
  | 'enrollment_completed'
  | 'enrollment_failed';
export type PasskeyEnrollmentDurationBucket =
  'under_1s' | '1_to_5s' | '5_to_15s' | '15_to_60s' | 'over_60s';
export type PasskeyEnrollmentRumRestartMode =
  'replay' | 'tag-only' | 'cancelled';
export type PasskeyEnrollmentRumOrigin = 'post_signup' | 'post_login';

interface PasskeyEnrollmentRumContext {
  attemptNumber?: number;
  durationBucket?: PasskeyEnrollmentDurationBucket;
  errorName?: string;
  errorShape?: string;
  failureCategory?: string;
}

const PASSKEY_REGISTRATION_OFFER_VARIANT = 'offer_passkey';
const PASSKEY_RUM_RENEWAL_FAILURE_STORAGE_KEY =
  'descope-universal-login:passkey-rum-renewal-failure';
const PASSKEY_RUM_RENEWAL_FAILURE_MAX_AGE_MS = 10 * 60 * 1000;
const TERMINAL_PASSKEY_ENROLLMENT_STATES = new Set<PasskeyEnrollmentRumState>([
  'enrollment_cancelled',
  'enrollment_completed',
  'enrollment_failed',
]);
let pendingPasskeyRumRenewalFailureAt: number | undefined;

const persistPasskeyRumRenewalFailure = (): void => {
  const occurredAt = Date.now();
  pendingPasskeyRumRenewalFailureAt = occurredAt;
  try {
    window.sessionStorage.setItem(
      PASSKEY_RUM_RENEWAL_FAILURE_STORAGE_KEY,
      String(occurredAt)
    );
  } catch {
    // The in-memory marker still reports after a later renewal in this page.
  }
};

const clearPersistedPasskeyRumRenewalFailure = (): void => {
  pendingPasskeyRumRenewalFailureAt = undefined;
  try {
    window.sessionStorage.removeItem(PASSKEY_RUM_RENEWAL_FAILURE_STORAGE_KEY);
  } catch {
    // The in-memory marker is already cleared.
  }
};

const getPendingPasskeyRumRenewalFailureAt = (): number | undefined => {
  let occurredAt = pendingPasskeyRumRenewalFailureAt;
  if (occurredAt === undefined) {
    try {
      const storedValue = window.sessionStorage.getItem(
        PASSKEY_RUM_RENEWAL_FAILURE_STORAGE_KEY
      );
      occurredAt = storedValue === null ? undefined : Number(storedValue);
    } catch {
      return undefined;
    }
  }

  const age = occurredAt === undefined ? undefined : Date.now() - occurredAt;
  if (
    occurredAt === undefined ||
    !Number.isFinite(occurredAt) ||
    occurredAt <= 0 ||
    age === undefined ||
    age < 0 ||
    age > PASSKEY_RUM_RENEWAL_FAILURE_MAX_AGE_MS
  ) {
    clearPersistedPasskeyRumRenewalFailure();
    return undefined;
  }

  return occurredAt;
};

const reportPendingPasskeyRumRenewalFailure = async (): Promise<void> => {
  if (
    !clientRumReporter ||
    getPendingPasskeyRumRenewalFailureAt() === undefined
  ) {
    return;
  }

  for (let attempt = 0; attempt < 40; attempt += 1) {
    if (clientRumReporter.getInternalContext()?.session_id) {
      if (getPendingPasskeyRumRenewalFailureAt() === undefined) {
        return;
      }
      clearPersistedPasskeyRumRenewalFailure();
      reportClientError(
        'Passkey enrollment RUM session renewal timed out',
        new Error('Datadog did not renew the stopped RUM session'),
        {
          flow: 'universal-login',
          reason: 'passkey-rum-renewal-timeout',
          source: 'clientMonitoring',
          status: 'deferred',
        }
      );
      return;
    }
    await new Promise(resolve => window.setTimeout(resolve, 25));
  }
};

type DescopeResourceTiming = PerformanceResourceTiming & {
  responseStatus?: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getStringField = (
  value: Record<string, unknown>,
  key: string
): string | undefined =>
  typeof value[key] === 'string' ? (value[key] as string) : undefined;

const getOptionalEnv = (key: keyof ImportMetaEnv): string | undefined => {
  const value = import.meta.env[key];
  return value === '' ? undefined : value;
};

const getConfiguredActivityUrlExclusions = (): NonNullable<
  RumInitConfiguration['excludedActivityUrls']
> =>
  [
    getOptionalEnv('VITE_BROWSER_FINGERPRINTING_JS_URL'),
    getOptionalEnv('VITE_CEP_JS_URL'),
  ].reduce<NonNullable<RumInitConfiguration['excludedActivityUrls']>>(
    (excludedActivityUrls, url) =>
      url ? [...excludedActivityUrls, url] : excludedActivityUrls,
    DEFAULT_EXCLUDED_ACTIVITY_URLS
  );

const parseSampleRate = (
  value: string | undefined,
  fallback: number
): number => {
  if (value === undefined) {
    return fallback;
  }

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return fallback;
  }

  return Math.min(100, Math.max(0, parsedValue));
};

const isAutomaticErrorEvent = (source: string | undefined): boolean =>
  source === 'console' || source === 'source';

const isExpectedDescopeValidationError = (message: string): boolean =>
  message.includes('[Descope]') &&
  (DESCOPE_RESULT_FAIL_ERROR_PATTERN.test(message) ||
    EXPECTED_DESCOPE_ERROR_PATTERNS.some(pattern => pattern.test(message)));

const getErrorValueText = (
  value: unknown
): { message: string; stack: string } => {
  if (value instanceof Error) {
    return {
      message: value.message,
      stack: value.stack ?? '',
    };
  }

  if (typeof value === 'string') {
    return { message: value, stack: '' };
  }

  if (isRecord(value)) {
    return {
      message:
        getStringField(value, 'message') ??
        getStringField(value, 'error') ??
        getStringField(value, 'detail') ??
        '',
      stack: getStringField(value, 'stack') ?? '',
    };
  }

  return { message: '', stack: '' };
};

const isDescopeUiLoadFailure = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);

  return [message, stack, fallbackMessage].some(text =>
    text.includes(DESCOPE_UI_LOAD_FAILURE_MESSAGE)
  );
};

const getHostFromUrl = (value: string): string | undefined => {
  try {
    const baseUrl =
      typeof window !== 'undefined'
        ? window.location.href
        : 'https://linktr.ee';

    return new URL(value, baseUrl).host || undefined;
  } catch {
    return undefined;
  }
};

const getFiniteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.round(value)
    : undefined;

const getNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const getDescopeUiScriptElements = (): HTMLScriptElement[] => {
  if (typeof document === 'undefined') {
    return [];
  }

  try {
    return Array.from(
      document.querySelectorAll<HTMLScriptElement>(DESCOPE_UI_SCRIPT_SELECTOR)
    );
  } catch {
    return [];
  }
};

const getDescopeUiScriptStatus = (
  script: HTMLScriptElement | undefined
): string => {
  if (!script) {
    return 'missing';
  }

  return script.getAttribute('status') ?? (script.src ? 'present' : 'no-src');
};

const getLatestDescopeUiResourceTiming = ():
  DescopeResourceTiming | undefined => {
  if (
    typeof performance === 'undefined' ||
    typeof performance.getEntriesByType !== 'function'
  ) {
    return undefined;
  }

  try {
    return performance
      .getEntriesByType('resource')
      .reduce<DescopeResourceTiming | undefined>((latestEntry, entry) => {
        const resourceEntry = entry as DescopeResourceTiming;

        if (!DESCOPE_UI_RESOURCE_PATH_PATTERN.test(resourceEntry.name)) {
          return latestEntry;
        }

        if (!latestEntry || resourceEntry.startTime >= latestEntry.startTime) {
          return resourceEntry;
        }

        return latestEntry;
      }, undefined);
  } catch {
    return undefined;
  }
};

const getDescopeUiLoadDiagnostics = (): SafeClientErrorContext => {
  const scripts = getDescopeUiScriptElements();
  const script = scripts[scripts.length - 1];
  const resourceTiming = getLatestDescopeUiResourceTiming();

  return {
    diagnostic: 'descope-ui-load-failure',
    descopeUiGlobalPresent:
      typeof (globalThis as { DescopeUI?: unknown }).DescopeUI !== 'undefined',
    descopeUiReplaceAllSupported: 'replaceAll' in String.prototype,
    descopeUiScriptCount: scripts.length,
    descopeUiScriptCrossOrigin: getNonEmptyString(script?.crossOrigin),
    descopeUiScriptHost: script?.src ? getHostFromUrl(script.src) : undefined,
    descopeUiScriptIntegrityPresent: script ? Boolean(script.integrity) : false,
    descopeUiScriptStatus: getDescopeUiScriptStatus(script),
    descopeUiResourceCompressedBytes: getFiniteNumber(
      resourceTiming?.encodedBodySize
    ),
    descopeUiResourceDurationMs: getFiniteNumber(resourceTiming?.duration),
    descopeUiResourceUncompressedBytes: getFiniteNumber(
      resourceTiming?.decodedBodySize
    ),
    descopeUiResourceHost: resourceTiming?.name
      ? getHostFromUrl(resourceTiming.name)
      : undefined,
    descopeUiResourceInitiatorType: getNonEmptyString(
      resourceTiming?.initiatorType
    ),
    descopeUiResourceResponseStatus: getFiniteNumber(
      resourceTiming?.responseStatus
    ),
    descopeUiResourceTransferSize: getFiniteNumber(
      resourceTiming?.transferSize
    ),
  };
};

const getClientErrorContext = (
  source: string,
  value: unknown,
  fallbackMessage = ''
): SafeClientErrorContext => ({
  source,
  ...(isDescopeUiLoadFailure(value, fallbackMessage)
    ? getDescopeUiLoadDiagnostics()
    : {}),
});

const isInjectedWebKitBridgeError = (message: string, stack = ''): boolean => {
  // Instagram/Facebook iOS in-app browsers inject a native bridge script on
  // pagehide. When the WebKit handler is missing it throws from the document
  // URL, even though the script is not served by this app.
  return (
    WEBKIT_MESSAGE_HANDLERS_ERROR_PATTERN.test(message) ||
    WEBKIT_MESSAGE_HANDLERS_ERROR_PATTERN.test(stack)
  );
};

const isInjectedWebKitBridgeErrorValue = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);
  return isInjectedWebKitBridgeError(message || fallbackMessage, stack);
};

const isInjectedAppendChildCircularJsonError = (
  message: string,
  stack = ''
): boolean => {
  return (
    CIRCULAR_REACT_DOM_NODE_ERROR_PATTERN.test(message) &&
    ANONYMOUS_APPEND_CHILD_JSON_STACK_PATTERN.test(stack)
  );
};

const isInjectedAppendChildCircularJsonErrorValue = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);
  return isInjectedAppendChildCircularJsonError(
    message || fallbackMessage,
    stack
  );
};

const isGenericCrossOriginScriptError = (
  message: string,
  stack = ''
): boolean => {
  const normalizedMessage = message.trim();

  if (!GENERIC_CROSS_ORIGIN_SCRIPT_ERROR_MESSAGES.has(normalizedMessage)) {
    return false;
  }

  if (normalizedMessage === 'Uncaught browser error Script error.') {
    return true;
  }

  const normalizedStack = stack.trim();

  return (
    normalizedStack.length === 0 ||
    normalizedStack === 'Error: Script error.' ||
    /(?:^|\n)\s*at undefined @\s*$/i.test(normalizedStack)
  );
};

const isGenericCrossOriginScriptErrorValue = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);
  return isGenericCrossOriginScriptError(message || fallbackMessage, stack);
};

const isResizeObserverLoopError = (message: string, stack = ''): boolean =>
  RESIZE_OBSERVER_LOOP_ERROR_PATTERN.test(message) ||
  RESIZE_OBSERVER_LOOP_ERROR_PATTERN.test(stack);

const isResizeObserverLoopErrorValue = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);
  return isResizeObserverLoopError(message || fallbackMessage, stack);
};

const shouldDropRumEvent = (event: MutableRumEvent): boolean => {
  const message = event.error?.message ?? '';
  const stack = event.error?.stack ?? '';
  const source = event.error?.source;

  if (
    isInjectedWebKitBridgeError(message, stack) ||
    isInjectedAppendChildCircularJsonError(message, stack) ||
    isGenericCrossOriginScriptError(message, stack) ||
    isResizeObserverLoopError(message, stack)
  ) {
    return true;
  }

  if (!isAutomaticErrorEvent(source)) {
    return false;
  }

  return (
    STATSIG_NETWORK_ERROR_PATTERN.test(message) ||
    isExpectedDescopeValidationError(message)
  );
};

export const sanitizeRumEvent = (event: RumEvent): boolean => {
  const mutableEvent = event as MutableRumEvent;

  if (shouldDropRumEvent(mutableEvent)) {
    return false;
  }

  if (
    isDescopeUiLoadFailure({
      message: mutableEvent.error?.message,
      stack: mutableEvent.error?.stack,
    })
  ) {
    mutableEvent.context = {
      ...mutableEvent.context,
      ...getDescopeUiLoadDiagnostics(),
    };
  }

  if (mutableEvent.view?.url) {
    mutableEvent.view.url = sanitizeClientErrorUrl(mutableEvent.view.url);
  }

  if (mutableEvent.view?.referrer) {
    mutableEvent.view.referrer = sanitizeClientErrorUrl(
      mutableEvent.view.referrer
    );
  }

  if (mutableEvent.resource?.url) {
    mutableEvent.resource.url = sanitizeClientErrorUrl(
      mutableEvent.resource.url
    );
  }

  if (mutableEvent.error?.message) {
    mutableEvent.error.message = redactClientErrorString(
      mutableEvent.error.message
    );
  }

  if (mutableEvent.error?.stack) {
    mutableEvent.error.stack = redactClientErrorString(
      mutableEvent.error.stack
    );
  }

  if (mutableEvent.action?.target?.name) {
    mutableEvent.action.target.name = '[REDACTED_ACTION]';
  }

  return true;
};

export const getClientMonitoringConfig = ():
  ClientMonitoringConfig | undefined => {
  if (getOptionalEnv('VITE_DATADOG_RUM_ENABLED') === 'false') {
    return undefined;
  }

  const applicationId = getOptionalEnv('VITE_DATADOG_RUM_APPLICATION_ID');
  const clientToken = getOptionalEnv('VITE_DATADOG_CLIENT_TOKEN');

  if (!applicationId || !clientToken) {
    return undefined;
  }

  if (
    import.meta.env.DEV &&
    getOptionalEnv('VITE_DATADOG_RUM_ENABLED') !== 'true'
  ) {
    return undefined;
  }

  return {
    applicationId,
    clientToken,
    site: getOptionalEnv('VITE_DATADOG_SITE') ?? DEFAULT_DATADOG_SITE,
    service: getOptionalEnv('VITE_DATADOG_SERVICE') ?? DEFAULT_DATADOG_SERVICE,
    env:
      getOptionalEnv('VITE_DATADOG_ENV') ??
      getOptionalEnv('VITE_STATSIG_STAGE') ??
      import.meta.env.MODE,
    version: getOptionalEnv('VITE_BUILD_VERSION') ?? 'local',
    sessionSampleRate: parseSampleRate(
      getOptionalEnv('VITE_DATADOG_RUM_SESSION_SAMPLE_RATE'),
      DEFAULT_SESSION_SAMPLE_RATE
    ),
    sessionReplaySampleRate: parseSampleRate(
      getOptionalEnv('VITE_DATADOG_RUM_REPLAY_SAMPLE_RATE'),
      DEFAULT_SESSION_REPLAY_SAMPLE_RATE
    ),
    excludedActivityUrls: getConfiguredActivityUrlExclusions(),
  };
};

export const initializeClientMonitoring = async (): Promise<boolean> => {
  if (hasInitializedClientMonitoring) {
    return true;
  }

  const config = getClientMonitoringConfig();
  if (!config) {
    return false;
  }

  try {
    const { datadogRum } = await import('@datadog/browser-rum');
    const initConfig: RumInitConfiguration = {
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site,
      service: config.service,
      env: config.env,
      version: config.version,
      sessionSampleRate: config.sessionSampleRate,
      sessionReplaySampleRate: config.sessionReplaySampleRate,
      trackUserInteractions: false,
      trackResources: true,
      trackLongTasks: true,
      excludedActivityUrls: config.excludedActivityUrls,
      defaultPrivacyLevel: 'mask-user-input',
      beforeSend: sanitizeRumEvent,
      silentMultipleInit: true,
    };

    datadogRum.init(initConfig);
    setClientErrorReporter(datadogRum as Pick<DatadogRum, 'addError'>);
    clientTimingReporter = datadogRum;
    clientRumReporter = datadogRum;
    void reportPendingPasskeyRumRenewalFailure();
    pendingClientTimings.forEach(({ name, time }) => {
      datadogRum.addTiming(name, time);
    });
    pendingClientTimings = [];

    if (config.sessionReplaySampleRate > 0) {
      datadogRum.startSessionReplayRecording();
    }

    hasInitializedClientMonitoring = true;
    return true;
  } catch (error) {
    reportClientError('Datadog RUM failed to initialise', error, {
      source: 'clientMonitoring',
    });
    return false;
  }
};

export const addClientTiming = (
  name: string,
  time = performance.now()
): void => {
  if (clientTimingReporter) {
    clientTimingReporter.addTiming(name, time);
    return;
  }

  pendingClientTimings.push({ name, time });
};

export const recordPasskeyRegistrationRumVariant = (
  variant: PasskeyRegistrationRumVariant,
  experiment: PasskeyRegistrationRumExperiment = PASSKEY_REGISTER_EXPERIMENT
): void => {
  if (!clientRumReporter) {
    return;
  }

  clientRumReporter.addFeatureFlagEvaluation(experiment, variant);
  clientRumReporter.addAction('passkey_registration_variant_resolved', {
    experiment,
    flow: 'universal-login',
    variant,
  });
};

export const getPasskeyEnrollmentDurationBucket = (
  durationMs: number
): PasskeyEnrollmentDurationBucket => {
  if (durationMs < 1000) {
    return 'under_1s';
  }
  if (durationMs < 5000) {
    return '1_to_5s';
  }
  if (durationMs < 15000) {
    return '5_to_15s';
  }
  if (durationMs < 60000) {
    return '15_to_60s';
  }
  return 'over_60s';
};

export const stopPasskeyEnrollmentRumReplay = (): void => {
  clientRumReporter?.stopSessionReplayRecording();
};

const getPasskeyEnrollmentExperiment = (
  enrollmentOrigin: PasskeyEnrollmentRumOrigin
): PasskeyRegistrationRumExperiment =>
  enrollmentOrigin === 'post_login'
    ? PASSKEY_REGISTER_POST_LOGIN_EXPERIMENT
    : PASSKEY_REGISTER_EXPERIMENT;

const tagPasskeyEnrollmentRumSession = (
  enrollmentOrigin: PasskeyEnrollmentRumOrigin
): void => {
  clientRumReporter?.addFeatureFlagEvaluation(
    getPasskeyEnrollmentExperiment(enrollmentOrigin),
    PASSKEY_REGISTRATION_OFFER_VARIANT
  );
};

export const startPasskeyEnrollmentRumReplay = (
  enrollmentOrigin: PasskeyEnrollmentRumOrigin = 'post_signup'
): void => {
  void reportPendingPasskeyRumRenewalFailure();
  tagPasskeyEnrollmentRumSession(enrollmentOrigin);
  clientRumReporter?.startSessionReplayRecording({ force: true });
};

export const restartPasskeyEnrollmentRumSession = async (
  reportRenewalFailure: () => void,
  getMode: () => PasskeyEnrollmentRumRestartMode = () => 'replay',
  enrollmentOrigin: PasskeyEnrollmentRumOrigin = 'post_signup'
): Promise<void> => {
  if (!clientRumReporter || getMode() === 'cancelled') {
    return;
  }

  // stopSession() renews on activity. A bfcache restore has no guaranteed user
  // event, so explicitly request renewal and wait for the new SDK session
  // before re-tagging and forcing replay.
  clientRumReporter.setTrackingConsent('granted');
  for (let attempt = 0; attempt < 40; attempt += 1) {
    const mode = getMode();
    if (mode === 'cancelled') {
      return;
    }
    if (clientRumReporter.getInternalContext()?.session_id) {
      void reportPendingPasskeyRumRenewalFailure();
      tagPasskeyEnrollmentRumSession(enrollmentOrigin);
      if (mode === 'replay') {
        clientRumReporter.startSessionReplayRecording({ force: true });
      }
      return;
    }
    await new Promise(resolve => window.setTimeout(resolve, 25));
  }

  if (getMode() === 'cancelled') {
    return;
  }
  persistPasskeyRumRenewalFailure();
  try {
    reportRenewalFailure();
  } catch {
    // The bounded marker remains available as a second reporting chance.
  }
};

export const endPasskeyEnrollmentRumSession = (): void => {
  stopPasskeyEnrollmentRumReplay();
  // A forced-replay decision is session state. Ending the cohort session
  // clears it before another page or RUM application can reuse the session.
  clientRumReporter?.stopSession();
};

export const recordPasskeyEnrollmentRumAction = (
  state: PasskeyEnrollmentRumState,
  context: PasskeyEnrollmentRumContext = {},
  enrollmentOrigin: PasskeyEnrollmentRumOrigin = 'post_signup'
): void => {
  if (!clientRumReporter) {
    return;
  }

  clientRumReporter.addAction(`passkey_${state}`, {
    enrollmentOrigin,
    experiment: getPasskeyEnrollmentExperiment(enrollmentOrigin),
    flow: 'universal-login',
    variant: PASSKEY_REGISTRATION_OFFER_VARIANT,
    ...context,
  });

  if (state === 'enrollment_failed') {
    // Preserve the forced, experiment-tagged session across retries. Ending it
    // here would require an asynchronous renewal during the retry click.
    stopPasskeyEnrollmentRumReplay();
  } else if (TERMINAL_PASSKEY_ENROLLMENT_STATES.has(state)) {
    endPasskeyEnrollmentRumSession();
  }
};

export const registerGlobalClientErrorHandlers = (): (() => void) => {
  if (removeGlobalErrorHandlers) {
    return removeGlobalErrorHandlers;
  }

  const handleError = (event: ErrorEvent) => {
    if (
      isInjectedWebKitBridgeErrorValue(event.error, event.message) ||
      isInjectedAppendChildCircularJsonErrorValue(event.error, event.message) ||
      isGenericCrossOriginScriptErrorValue(event.error, event.message) ||
      isResizeObserverLoopErrorValue(event.error, event.message)
    ) {
      return;
    }

    reportClientError('Uncaught browser error', event.error ?? event.message, {
      ...getClientErrorContext('window.onerror', event.error, event.message),
    });
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    reportClientError('Unhandled promise rejection', event.reason, {
      ...getClientErrorContext('unhandledrejection', event.reason),
    });
  };

  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  removeGlobalErrorHandlers = () => {
    window.removeEventListener('error', handleError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    removeGlobalErrorHandlers = undefined;
  };

  return removeGlobalErrorHandlers;
};

export const resetClientMonitoringForTests = (): void => {
  hasInitializedClientMonitoring = false;
  clientTimingReporter = undefined;
  clientRumReporter = undefined;
  pendingPasskeyRumRenewalFailureAt = undefined;
  pendingClientTimings = [];
  removeGlobalErrorHandlers?.();
};
